// src/components/admin/performance/PerformanceReviewsTable.js
import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, Eye, ChevronDown, ChevronRight, Star, Calendar, User, Briefcase, AlertCircle } from 'lucide-react';
import performanceService from '../../../services/performance.service';
import PerformanceFilters from './PerformanceFilters';
import ReviewDetailsModal from './ReviewDetailsModal';

const PerformanceReviewsTable = () => {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({});
    const [showFilters, setShowFilters] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [selectedReview, setSelectedReview] = useState(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchReviews();
    }, [filters]);

    const fetchReviews = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await performanceService.getAllReviews(filters);
            setReviews(data.reviews || []);
        } catch (err) {
            console.error('Error fetching reviews:', err);
            setError('Failed to load performance reviews');
        } finally {
            setLoading(false);
        }
    };

    const toggleRowExpansion = (employeeId) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(employeeId)) {
                newSet.delete(employeeId);
            } else {
                newSet.add(employeeId);
            }
            return newSet;
        });
    };

    const handleViewReview = (review) => {
        setSelectedReview(review);
        setShowReviewModal(true);
    };

    const handleExportCSV = () => {
        // CSV export logic
        const csvData = reviews.map(emp => ({
            'Employee Name': `${emp.first_name} ${emp.last_name}`,
            'Employee Code': emp.employee_code,
            'Designation': emp.designation,
            'Department': emp.department || 'N/A',
            'Manager': emp.manager_first_name ? `${emp.manager_first_name} ${emp.manager_last_name}` : 'N/A',
            'Latest Review Date': emp.latest_review ? new Date(emp.latest_review.created_at).toLocaleDateString() : 'N/A',
            'Rating': emp.latest_review ? emp.latest_review.overall_rating : 'N/A',
            'Review Type': emp.latest_review ? (emp.latest_review.review_type || 'manager_review').replace('_', ' ') : 'N/A',
            'Status': emp.latest_review ? emp.latest_review.status : 'N/A',
            'Total Reviews': emp.all_reviews.length
        }));

        const csvContent = [
            Object.keys(csvData[0]).join(','),
            ...csvData.map(row => Object.values(row).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `performance_reviews_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    const filteredReviews = reviews.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.employee_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.designation && emp.designation.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Confirmed Reviews</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        View all admin-confirmed performance reviews (both self-reviews and manager reviews)
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                    >
                        <Filter className="w-4 h-4 mr-2" />
                        Filters
                    </button>
                    <button
                        onClick={handleExportCSV}
                        className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        Export CSV
                    </button>
                </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
                <PerformanceFilters
                    filters={filters}
                    onFiltersChange={setFilters}
                    onClose={() => setShowFilters(false)}
                />
            )}

            {/* Search Bar */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, code, or designation..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                />
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total Employees</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {filteredReviews.length}
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Reviewed</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {filteredReviews.filter(emp => emp.latest_review).length}
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Average Rating</div>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                        {filteredReviews.filter(emp => emp.latest_review).length > 0
                            ? (filteredReviews
                                .filter(emp => emp.latest_review)
                                .reduce((sum, emp) => sum + parseFloat(emp.latest_review.overall_rating), 0) /
                                filteredReviews.filter(emp => emp.latest_review).length
                            ).toFixed(1)
                            : 'N/A'
                        }
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : filteredReviews.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                        <User className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <p>No performance reviews found</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-900">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Employee
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Department
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Manager
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Latest Review
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Rating
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Type
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {filteredReviews.map((emp) => (
                                    <React.Fragment key={emp.employee_id}>
                                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <button
                                                        onClick={() => toggleRowExpansion(emp.employee_id)}
                                                        className="mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                    >
                                                        {expandedRows.has(emp.employee_id) ? (
                                                            <ChevronDown className="w-4 h-4" />
                                                        ) : (
                                                            <ChevronRight className="w-4 h-4" />
                                                        )}
                                                    </button>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {emp.first_name} {emp.last_name}
                                                        </div>
                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                            {emp.employee_code} • {emp.designation}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {emp.department || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {emp.manager_first_name ? `${emp.manager_first_name} ${emp.manager_last_name}` : 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                {emp.latest_review ? (
                                                    <div className="flex items-center">
                                                        <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                                                        {new Date(emp.latest_review.created_at).toLocaleDateString()}
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">No reviews</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {emp.latest_review ? (
                                                    <div className="flex items-center">
                                                        <Star className="w-4 h-4 mr-1 text-yellow-400" />
                                                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {parseFloat(emp.latest_review.overall_rating).toFixed(1)}/5.0
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {emp.latest_review ? (
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                        emp.latest_review.review_type === 'self_review'
                                                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                                                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                                    }`}>
                                                        {emp.latest_review.review_type === 'self_review' ? 'Self Review' : 'Manager Review'}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                {emp.latest_review && (
                                                    <button
                                                        onClick={() => handleViewReview(emp.latest_review)}
                                                        className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                                    >
                                                        <Eye className="w-4 h-4 mr-1" />
                                                        View
                                                    </button>
                                                )}
                                            </td>
                                        </tr>

                                        {/* Expanded Row - All Reviews */}
                                        {expandedRows.has(emp.employee_id) && emp.all_reviews.length > 0 && (
                                            <tr>
                                                <td colSpan="7" className="px-6 py-4 bg-gray-50 dark:bg-gray-900">
                                                    <div className="space-y-2">
                                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                                                            Review History ({emp.all_reviews.length})
                                                        </h4>
                                                        <div className="space-y-2">
                                                            {emp.all_reviews.map((review) => (
                                                                <div
                                                                    key={review.id}
                                                                    className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700"
                                                                >
                                                                    <div className="flex items-center gap-4">
                                                                        <div className="text-sm">
                                                                            <span className="text-gray-600 dark:text-gray-400">
                                                                                {new Date(review.review_period_start).toLocaleDateString()} - {new Date(review.review_period_end).toLocaleDateString()}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex items-center">
                                                                            <Star className="w-4 h-4 mr-1 text-yellow-400" />
                                                                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                                                {parseFloat(review.overall_rating).toFixed(1)}
                                                                            </span>
                                                                        </div>
                                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                                            review.review_type === 'self_review'
                                                                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                                                                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                                                        }`}>
                                                                            {review.review_type === 'self_review' ? 'Self' : 'Manager'}
                                                                        </span>
                                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                                                            review.status === 'completed'
                                                                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                                                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                                                        }`}>
                                                                            {review.status}
                                                                        </span>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleViewReview(review)}
                                                                        className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                                                    >
                                                                        <Eye className="w-4 h-4 mr-1" />
                                                                        View Details
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Review Details Modal */}
            {showReviewModal && selectedReview && (
                <ReviewDetailsModal
                    review={selectedReview}
                    onClose={() => {
                        setShowReviewModal(false);
                        setSelectedReview(null);
                    }}
                />
            )}
        </div>
    );
};

export default PerformanceReviewsTable;
